import { formatDateValue } from "utils/date-format";
import { SharePointConsentRequiredError, SharePointItem } from "./definitions";

const CONSENT_REQUIRED_TYPE = "SharePointConsentRequiredException";
const NO_ACCESS_TYPE = "SharePointNoAccessException";
const FORBIDDEN_TYPE = "ForbiddenException";

// Office and PDF names are product names, so they are not translated.
const PRODUCT_TYPE_BY_EXTENSION: Record<string, string> = {
  xlsx: "Excel",
  xls: "Excel",
  xlsm: "Excel",
  docx: "Word",
  doc: "Word",
  pptx: "PowerPoint",
  ppt: "PowerPoint",
  pdf: "PDF",
};

const PRODUCT_TYPE_BY_MIME: Array<[RegExp, string]> = [
  [/spreadsheet|excel/i, "Excel"],
  [/wordprocessing|msword|\bword\b/i, "Word"],
  [/presentation|powerpoint/i, "PowerPoint"],
  [/pdf/i, "PDF"],
];

/** Friendly type label: product name, else the uppercased extension. */
export const getItemTypeLabel = (
  item: SharePointItem,
  t: (key: string) => string,
): string => {
  if (item.isFolder) {
    return t("components.sharepoint_browser.folder");
  }
  const extension = item.name.includes(".")
    ? item.name.split(".").pop()?.toLowerCase()
    : undefined;
  if (extension && PRODUCT_TYPE_BY_EXTENSION[extension]) {
    return PRODUCT_TYPE_BY_EXTENSION[extension];
  }
  if (item.mimeType) {
    for (const [pattern, label] of PRODUCT_TYPE_BY_MIME) {
      if (pattern.test(item.mimeType)) {
        return label;
      }
    }
  }
  return extension
    ? extension.toUpperCase()
    : t("components.sharepoint_browser.fileType");
};

/** Calendar date for an epoch-seconds timestamp, in the team date format. */
export const formatModifiedDate = (epochSeconds?: number): string =>
  epochSeconds ? formatDateValue(epochSeconds) : "—";

export const formatSize = (size?: number): string => {
  if (size === undefined || size === null) {
    return "—";
  }
  if (size < 1024) {
    return `${size} B`;
  }
  const units = ["KB", "MB", "GB", "TB"];
  let value = size / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * Returns the consent details when an RTK Query error is a
 * SharePointConsentRequiredException. API Gateway may rewrite the status, so
 * the error type in the body is what counts.
 */
export const getConsentRequiredError = (
  error: unknown,
): SharePointConsentRequiredError | undefined => {
  if (!error || typeof error !== "object" || !("data" in error)) {
    return undefined;
  }
  const data = (error as { data?: unknown }).data;
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const body = data as Record<string, unknown>;
  const type = typeof body.__type === "string" ? body.__type : "";
  if (!type.endsWith(CONSENT_REQUIRED_TYPE)) {
    return undefined;
  }
  return {
    message: typeof body.message === "string" ? body.message : undefined,
    authUrl: typeof body.authUrl === "string" ? body.authUrl : undefined,
    state: typeof body.state === "string" ? body.state : undefined,
  };
};

/**
 * True when an RTK Query error is a SharePointNoAccessException: the caller
 * cannot open the file in SharePoint. Told apart from other 403s by type.
 */
export const isNoAccessError = (error: unknown): boolean => {
  if (!error || typeof error !== "object" || !("data" in error)) {
    return false;
  }
  const data = (error as { data?: unknown }).data;
  const type =
    data && typeof data === "object"
      ? (data as Record<string, unknown>).__type
      : undefined;
  return typeof type === "string" && type.endsWith(NO_ACCESS_TYPE);
};

/**
 * True when an RTK Query error is a 403 or ForbiddenException that is not a
 * consent request, which also arrives as a 403.
 */
export const isForbiddenError = (error: unknown): boolean => {
  if (!error || typeof error !== "object" || getConsentRequiredError(error)) {
    return false;
  }
  const { status, data } = error as { status?: unknown; data?: unknown };
  if (status === 403) {
    return true;
  }
  const type =
    data && typeof data === "object"
      ? (data as Record<string, unknown>).__type
      : undefined;
  return typeof type === "string" && type.endsWith(FORBIDDEN_TYPE);
};

const PREVIEW_HOST_SUFFIXES = [".sharepoint.com", ".officeapps.live.com"];

/**
 * The preview iframe runs scripts with its own origin, so only Microsoft's
 * SharePoint and Office viewer hosts may be embedded.
 */
export const isSharePointPreviewUrl = (value?: string): value is string => {
  if (!isHttpsUrl(value)) {
    return false;
  }
  const { hostname } = new URL(value);
  return (
    hostname === "sharepoint.com" ||
    PREVIEW_HOST_SUFFIXES.some((suffix) => hostname.endsWith(suffix))
  );
};

/** File content is only fetched from a SharePoint host over https. */
export const isSharePointContentUrl = (value?: string): value is string => {
  if (!isHttpsUrl(value)) {
    return false;
  }
  const { hostname } = new URL(value);
  return hostname === "sharepoint.com" || hostname.endsWith(".sharepoint.com");
};

/** Only https URLs are embedded or linked. */
export const isHttpsUrl = (value?: string): value is string => {
  if (!value) {
    return false;
  }
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};
